import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import { el } from 'date-fns/locale';
import { Plus, Settings as SettingsIcon, AlertTriangle, Clock, Calendar, CalendarDays, CalendarClock, Users, Briefcase, Bell, LayoutDashboard, FolderOpen, Search, X, ChevronDown, User, Building2, Wallet, Timer, Coins, LogOut, Shield, FileText, Trash2, BookOpen } from 'lucide-react';
import 'react-datepicker/dist/react-datepicker.css';
import DatePickerWithInput from '../components/DatePickerWithInput';
import { useAuth } from '../contexts/AuthContext';

// Status options for entries
const STATUS_OPTIONS = [
  { value: 'ΣΕ ΑΝΑΜΟΝΗ', label: 'ΣΕ ΑΝΑΜΟΝΗ' },
  { value: 'ΟΛΟΚΛΗΡΩΜΕΝΗ', label: 'ΟΛΟΚΛΗΡΩΜΕΝΗ' },
  { value: 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ', label: 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ' },
];

// Status options for ΔΙΚΑΣΙΜΟΣ entries
const HEARING_STATUS_OPTIONS = [
  { value: 'ΣΕ ΑΝΑΜΟΝΗ', label: 'ΣΕ ΑΝΑΜΟΝΗ' },
  { value: 'ΟΛΟΚΛΗΡΩΜΕΝΗ', label: 'ΟΛΟΚΛΗΡΩΜΕΝΗ' },
  { value: 'ΑΝΑΒΟΛΗ', label: 'ΑΝΑΒΟΛΗ' },
  { value: 'ΜΑΤΑΙΩΣΗ', label: 'ΜΑΤΑΙΩΣΗ' },
  { value: 'ΠΑΡΑΙΤΗΣΗ ΑΠΟ ΔΙΚΟΓΡΑΦΟ', label: 'ΠΑΡΑΙΤΗΣΗ' },
];

// Case description types mapping
const CASE_DESCRIPTION_LABELS = {
  'debt': 'Αγωγή χρέους',
  'property': 'Εμπράγματη αγωγή',
  'fraud': 'Αγωγή καταδολίευσης',
  'inheritance': 'Κληρονομική διαφορά',
  'divorce': 'Διαζύγιο',
  'alimony': 'Διατροφή',
  'custody': 'Επιμέλεια τέκνων',
  'lease': 'Μισθωτική διαφορά',
  'labor': 'Εργατική διαφορά',
  'tort': 'Αδικοπραξία',
  'contract': 'Συμβατική διαφορά',
  'unjust_enrichment': 'Αδικαιολόγητος πλουτισμός',
  'compensation': 'Αποζημίωση',
  'ownership': 'Αναγνώριση κυριότητας',
  'possession': 'Προστασία νομής',
  'easement': 'Δουλεία',
  'condominium': 'Διαφορά οροφοκτησίας',
  'insurance': 'Ασφαλιστική διαφορά',
  'medical': 'Ιατρική αμέλεια',
  'traffic': 'Τροχαίο ατύχημα',
  'other': 'Άλλο'
};

// Helper to remove Greek accents from uppercase text
const removeAccents = (str) => {
  if (!str) return '';
  const accentMap = {
    'Ά': 'Α', 'Έ': 'Ε', 'Ή': 'Η', 'Ί': 'Ι', 'Ό': 'Ο', 'Ύ': 'Υ', 'Ώ': 'Ω',
    'ά': 'Α', 'έ': 'Ε', 'ή': 'Η', 'ί': 'Ι', 'ό': 'Ο', 'ύ': 'Υ', 'ώ': 'Ω',
    'ϊ': 'Ι', 'ϋ': 'Υ', 'ΐ': 'Ι', 'ΰ': 'Υ', 'Ϊ': 'Ι', 'Ϋ': 'Υ'
  };
  return str.toUpperCase().split('').map(char => accentMap[char] || char).join('');
};

// Capitalize first letter, preserve known proper nouns (Κτηματολόγιο, etc.)
const capitalizeFirst = (str) => {
  if (!str) return '';
  const s = str.trim();
  let result;
  if (s === s.toUpperCase()) {
    result = s.toLowerCase();
  } else {
    result = s.split(/(\s+)/).map((w) => {
      const letters = w.replace(/[^\p{L}]/gu, '');
      if (letters.length >= 2 && letters === letters.toUpperCase()) return w;
      return w.toLowerCase();
    }).join('');
  }
  result = result.charAt(0).toUpperCase() + result.slice(1);
  const properNouns = ['Κτηματολόγιο', 'Άρειος Πάγος', 'ΣτΕ', 'Ελλάδα'];
  properNouns.forEach((noun) => {
    result = result.replace(new RegExp(noun, 'gi'), noun);
  });
  return result;
};

// Helper function for case description
const getCaseDescriptionLabel = (entry) => {
  if (entry.case_description_type === 'other' && entry.case_description_custom) {
    return entry.case_description_custom;
  }
  if (entry.case_description_type) {
    return CASE_DESCRIPTION_LABELS[entry.case_description_type] || entry.case_description_type;
  }
  return null;
};

import { toast } from 'sonner';
import EntryWizard from '../components/EntryWizard';
import EmptyState from '../components/EmptyState';
import MyrtoChat from '../components/MyrtoChat';
import OnboardingGuide from '../components/OnboardingGuide';
import EntriesList from '../components/EntriesList';
import EntryDetail from '../components/EntryDetail';
import EntriesSkeleton from '../components/EntriesSkeleton';
import CalendarView from '../components/CalendarView';
import FinancialManagementModal from '../components/FinancialManagementModal';
import OfficeTeamModal from '../components/OfficeTeamModal';
import TimeTrackerModal from '../components/TimeTrackerModal';
import CourtPlanLogo from '../components/CourtPlanLogo';
import Settings from './Settings';
import DayPlanner from './DayPlanner';
import SubscriptionTab from '../components/SubscriptionTab';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { Menu } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [billing, setBilling] = useState(null);
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isFinancialOpen, setIsFinancialOpen] = useState(false);
  const [isTeamOpen, setIsTeamOpen] = useState(false);
  const [isFinancialSheetOpen, setIsFinancialSheetOpen] = useState(false);
  const [isTimeTrackerOpen, setIsTimeTrackerOpen] = useState(false);
  const [hasActiveTimer, setHasActiveTimer] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState('profile');
  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Mobile-only accordion: which of the dashboard sections is expanded
  // On mobile: 'entries' (default — list of entries filtered by today/week) or 'clients'
  // Default to 'entries' so the user always sees something on first load
  const [mobileExpanded, setMobileExpanded] = useState('entries');
  const toggleMobile = (key) => setMobileExpanded((cur) => (cur === key ? null : key));


  // Legal news ticker
  const [legalNews, setLegalNews] = useState([]);
  const newsTodayKey = new Date().toISOString().slice(0, 10);
  const [newsDismissed, setNewsDismissed] = useState(() => localStorage.getItem('cp_news_dismissed') === newsTodayKey);
  const dismissNews = () => { localStorage.setItem('cp_news_dismissed', newsTodayKey); setNewsDismissed(true); };
  useEffect(() => {
    let cancelled = false;
    const loadNews = async () => {
      try {
        const res = await axios.get(`${API}/news/legal?limit=30`);
        if (!cancelled) setLegalNews(res.data?.items || []);
      } catch {}
    };
    loadNews();
    const id = setInterval(loadNews, 30 * 60 * 1000); // refresh every 30 min
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (loading || !user?.id) return;
    try {
      if (clients.length === 0 && !localStorage.getItem(`cp_onboarding_done_${user.id}`)) {
        setShowOnboarding(true);
      }
    } catch (_) {}
  }, [loading, user?.id, clients.length]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);

  // Άνοιγμα καρτέλας εγγραφής μέσω ?entry= (π.χ. από το Πρόγραμμα Ημέρας)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const entryId = params.get('entry');
    if (entryId && entries.length > 0) {
      const target = entries.find((e) => e.id === entryId);
      if (target) {
        setSelectedEntry(target);
        window.history.replaceState({}, '', '/dashboard');
      }
    }
  }, [entries]);
  const [viewMode, setViewMode] = useState('calendar'); // 'dashboard' or 'calendar'
  
  // Force dashboard view on mobile portrait, reset when rotating to portrait
  useEffect(() => {
    const handleResize = () => {
      const isPortrait = window.innerHeight > window.innerWidth || window.innerWidth < 640;
      if (isPortrait && viewMode === 'calendar') {
        setViewMode('dashboard');
      }
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [viewMode]);

  const isMobilePortrait = typeof window !== 'undefined' && (window.innerWidth < 640 || (window.innerHeight > window.innerWidth && window.innerHeight > 500));
  const effectiveViewMode = isMobilePortrait ? 'dashboard' : viewMode;
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [statsFilter, setStatsFilter] = useState(() => {
    // Mobile: default 'today'. Desktop: null (show all).
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches) {
      return 'today';
    }
    return null;
  }); // 'today', 'week', 'month', 'year' or null
  const [clientSearch, setClientSearch] = useState('');
  // Prefill data for new entry form when document type changes
  const [entryPrefillData, setEntryPrefillData] = useState(null);
  // Edit client modal state
  const [editingClient, setEditingClient] = useState(null);
  const [editClientForm, setEditClientForm] = useState({
    client_type: 'natural',
    name: '',
    surname: '',
    company_name: '',
    vat: '',
    phone: '',
    email: ''
  });
  // Status dropdown state for column 2
  const [openStatusDropdownCol2, setOpenStatusDropdownCol2] = useState(null);
  const [showScheduleDatePickerCol2, setShowScheduleDatePickerCol2] = useState(null);
  const [updatingStatusCol2, setUpdatingStatusCol2] = useState(null);
  const datePickerRefCol2 = useRef(null);
  const calendarRef = useRef(null);
  const calendarPanelRef = useRef(null);
  const [calPanelHeight, setCalPanelHeight] = useState(null);

  useEffect(() => {
    if (viewMode !== 'calendar') return undefined;
    const el = calendarPanelRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setCalPanelHeight(el.getBoundingClientRect().height));
    ro.observe(el);
    setCalPanelHeight(el.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, [viewMode]);
  const initialScrollDone = useRef(false);
  const mainScrollRef = useRef(null);

  // Scroll to top when dashboard loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-scroll to show full date picker when it opens in Column 2
  useEffect(() => {
    if (showScheduleDatePickerCol2 && datePickerRefCol2.current) {
      const timer = setTimeout(() => {
        if (datePickerRefCol2.current) {
          datePickerRefCol2.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center'
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showScheduleDatePickerCol2]);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRefCol2.current && !datePickerRefCol2.current.contains(event.target)) {
        setShowScheduleDatePickerCol2(null);
      }
    };

    if (showScheduleDatePickerCol2) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showScheduleDatePickerCol2]);

  const fetchBilling = async () => {
    try {
      const { data } = await axios.get(`${API}/billing/me`, { withCredentials: true });
      setBilling(data);
    } catch (e) {
      // silent
    }
  };

  const fetchData = async () => {
    try {
      const [entriesRes, clientsRes, casesRes] = await Promise.all([
        axios.get(`${API}/entries`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/cases`)
      ]);
      setEntries(entriesRes.data);
      setClients(clientsRes.data);
      setCases(casesRes.data);
      setLoading(false);
      fetchBilling();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Σφάλμα κατά τη φόρτωση δεδομένων');
      setLoading(false);
    }
  };

  // Mobile: when any modal/dialog/sheet is open, hide the fixed toolbars
  // (segmented control / search bar) below the logo so they don't show through.
  const isAnyMobileModalOpen = isFinancialOpen || isTimeTrackerOpen || isFinancialSheetOpen || isFormOpen || isSubscriptionOpen || !!editingClient || !!selectedEntry;

  const handleLogout = async () => {
    try { await logout(); } catch (e) { /* noop */ }
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    fetchData();
    // If user just returned from Stripe checkout, finalize the payment
    (async () => {
      try {
        const { handleStripeReturn } = await import('../lib/payments');
        await handleStripeReturn(fetchData);
      } catch (e) { /* noop */ }
    })();
  }, []);

  // Check for active timer on load
  useEffect(() => {
    const checkActiveTimer = () => {
      const savedTimer = localStorage.getItem('courtplan_active_timer');
      setHasActiveTimer(!!savedTimer);
    };
    checkActiveTimer();
    
    // Check periodically in case timer was started in another tab
    const interval = setInterval(checkActiveTimer, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleFormComplete = () => {
    fetchData();
    setIsFormOpen(false);
    setEntryPrefillData(null);
  };

  // Handle opening new entry form with prefilled data (when document type changes)
  const handleOpenNewEntryWithData = (prefillData) => {
    setEntryPrefillData(prefillData);
    setIsFormOpen(true);
  };

  // Handle opening edit client modal
  const handleEditClient = (client, e) => {
    e.stopPropagation();
    setEditClientForm({
      client_type: client.client_type || 'natural',
      name: client.name || '',
      surname: client.surname || '',
      company_name: client.company_name || '',
      vat: client.vat || '',
      phone: client.phone || '',
      email: client.email || ''
    });
    setEditingClient(client);
  };

  // Handle saving client edits
  const handleSaveClient = async () => {
    try {
      await axios.put(`${API}/clients/${editingClient.id}`, editClientForm);
      toast.success('Τα στοιχεία πελάτη ενημερώθηκαν');
      setEditingClient(null);
      fetchData();
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error('Σφάλμα κατά την ενημέρωση πελάτη');
    }
  };

  const handleDeleteEntry = async (id) => {
    try {
      await axios.delete(`${API}/entries/${id}`);
      toast.success('Η εγγραφή διαγράφηκε επιτυχώς');
      fetchData();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Σφάλμα κατά τη διαγραφή εγγραφής');
    }
  };

  const handleEntryClick = (entry) => {
    setSelectedEntry(entry);
  };

  // Handler to open case modal from EntryWizard
  const handleOpenCaseModal = (caseId) => {
    const caseEntries = entries.filter(e => e.case_id === caseId);
    if (caseEntries.length > 0) {
      // Set selected entry to first entry of this case to open the modal
      setSelectedEntry(caseEntries[0]);
    }
  };

  // Check if entry is a ΔΙΚΑΣΙΜΟΣ entry
  // Check if entry is a ΔΙΚΑΣΙΜΟΣ entry (handles accents)
  const isHearingEntry = (entry) => {
    if (!entry.title) return false;
    // Normalize: uppercase and remove Greek accents
    const normalized = entry.title.toUpperCase()
      .replace(/Ά/g, 'Α').replace(/Έ/g, 'Ε').replace(/Ή/g, 'Η')
      .replace(/Ί/g, 'Ι').replace(/Ό/g, 'Ο').replace(/Ύ/g, 'Υ').replace(/Ώ/g, 'Ω');
    return normalized === 'ΔΙΚΑΣΙΜΟΣ';
  };

  // Get status options based on entry type
  const getStatusOptionsForEntry = (entry) => {
    return isHearingEntry(entry) ? HEARING_STATUS_OPTIONS : STATUS_OPTIONS;
  };

  // Status helper functions for Column 1 & 2
  const getDisplayStatusCol2 = (entry) => {
    // For ΔΙΚΑΣΙΜΟΣ entries, use hearing status options
    if (isHearingEntry(entry)) {
      const hearingStatuses = HEARING_STATUS_OPTIONS.map(o => o.value);
      if (entry.status && hearingStatuses.includes(entry.status)) {
        if (entry.status === 'ΠΑΡΑΙΤΗΣΗ ΑΠΟ ΔΙΚΟΓΡΑΦΟ') return 'ΠΑΡΑΙΤΗΣΗ';
        return entry.status;
      }
      return 'ΣΕ ΑΝΑΜΟΝΗ';
    }
    const entryDate = new Date(entry.date);
    const today = new Date();
    entryDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    if (entryDate < today && entry.status !== 'ΟΛΟΚΛΗΡΩΜΕΝΗ') {
      return 'ΕΚΠΡΟΘΕΣΜΗ';
    }
    
    if (entry.status === 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ' && entry.scheduled_date) {
      const schedDate = new Date(entry.scheduled_date);
      const formattedDate = schedDate.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      return `ΠΡΟΓΡ. ${formattedDate}`;
    }
    
    const validStatuses = ['ΣΕ ΑΝΑΜΟΝΗ', 'ΟΛΟΚΛΗΡΩΜΕΝΗ', 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ'];
    if (entry.status && validStatuses.includes(entry.status)) {
      return entry.status;
    }
    
    return 'ΣΕ ΑΝΑΜΟΝΗ';
  };

  const isEntryExpiredCol2 = (entry) => {
    // ΔΙΚΑΣΙΜΟΣ entries are never auto-expired
    if (isHearingEntry(entry)) {
      return false;
    }
    const entryDate = new Date(entry.date);
    const today = new Date();
    entryDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return entryDate < today && entry.status !== 'ΟΛΟΚΛΗΡΩΜΕΝΗ';
  };

  const handleStatusChangeCol2 = async (entryId, newStatus, scheduledDate = null) => {
    if (newStatus === 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ' && !scheduledDate) {
      setShowScheduleDatePickerCol2(entryId);
      setOpenStatusDropdownCol2(null);
      return;
    }
    
    setUpdatingStatusCol2(entryId);
    try {
      const payload = { status: newStatus };
      if (newStatus === 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ' && scheduledDate) {
        payload.scheduled_date = scheduledDate;
      }
      await axios.patch(`${API}/entries/${entryId}/status`, payload);
      toast.success('Η κατάσταση ενημερώθηκε');
      setOpenStatusDropdownCol2(null);
      setShowScheduleDatePickerCol2(null);
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Σφάλμα κατά την ενημέρωση κατάστασης');
    } finally {
      setUpdatingStatusCol2(null);
    }
  };

  const handleScheduledDateSelectCol2 = (entryId, date) => {
    if (date) {
      const formattedDate = date.toISOString().split('T')[0];
      handleStatusChangeCol2(entryId, 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ', formattedDate);
    }
  };

  const getEntryDetails = () => {
    if (!selectedEntry) return null;

    const client = clients.find(c => c.id === selectedEntry.client_id);
    const caseData = cases.find(c => c.id === selectedEntry.case_id);
    
    // Find all related entries for the same case
    const relatedEntries = selectedEntry.case_id 
      ? entries.filter(e => e.case_id === selectedEntry.case_id)
      : [selectedEntry];

    return {
      entry: selectedEntry,
      client,
      caseData,
      relatedEntries
    };
  };

  // Dashboard Statistics
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const next7Days = new Date(today);
    next7Days.setDate(today.getDate() + 7);
    
    const next30Days = new Date(today);
    next30Days.setDate(today.getDate() + 30);

    // Exclude replaced entries from stats
    const activeEntries = entries.filter(e => {
      if (e.status === 'ΑΝΤΙΚΑΤΑΣΤΑΘΗΚΕ') {
        if (e.entry_type === 'hearing' || (e.title && e.title.toUpperCase().includes('ΔΙΚΑΣΙΜ'))) return true;
        return false;
      }
      return true;
    });

    const todayCount = activeEntries.filter(e => {
      const d = new Date(e.date);
      d.setHours(0, 0, 0, 0);
      return d.getTime() === today.getTime();
    }).length;

    const weekCount = activeEntries.filter(e => {
      const d = new Date(e.date);
      return d >= today && d <= next7Days;
    }).length;

    const monthCount = activeEntries.filter(e => {
      const d = new Date(e.date);
      return d >= today && d <= next30Days;
    }).length;

    const expiredCount = activeEntries.filter(e => {
      const d = new Date(e.date);
      d.setHours(0, 0, 0, 0);
      return d < today;
    }).length;

    return { todayCount, weekCount, monthCount, expiredCount };
  }, [entries]);

  // Non-expired entries (for both Dashboard and Calendar views)
  const nonExpiredEntries = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return entries.filter(e => {
      const d = new Date(e.date);
      d.setHours(0, 0, 0, 0);
      if (d < today) return false;
      // Hide replaced entries (ΑΝΤΙΚΑΤΑΣΤΑΘΗΚΕ) except hearings and completed
      if (e.status === 'ΑΝΤΙΚΑΤΑΣΤΑΘΗΚΕ') {
        if (e.entry_type === 'hearing' || (e.title && e.title.toUpperCase().includes('ΔΙΚΑΣΙΜ'))) return true;
        if (e.status === 'ΟΛΟΚΛΗΡΩΜΕΝΗ') return true;
        return false;
      }
      return true;
    });
  }, [entries]);

  // Filtered entries based on stats filter - excluding expired entries
  const filteredEntries = useMemo(() => {
    if (!statsFilter) return nonExpiredEntries;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (statsFilter === 'today') {
      return nonExpiredEntries.filter(e => {
        const d = new Date(e.date);
        d.setHours(0, 0, 0, 0);
        return d.getTime() === today.getTime();
      });
    }
    
    if (statsFilter === 'week') {
      const endOfWeek = new Date(today);
      endOfWeek.setDate(today.getDate() + 7);
      return nonExpiredEntries.filter(e => {
        const d = new Date(e.date);
        return d >= today && d <= endOfWeek;
      });
    }
    
    if (statsFilter === 'month') {
      const next30Days = new Date(today);
      next30Days.setDate(today.getDate() + 30);
      return nonExpiredEntries.filter(e => {
        const d = new Date(e.date);
        return d >= today && d <= next30Days;
      });
    }

    if (statsFilter === 'year') {
      const next365Days = new Date(today);
      next365Days.setDate(today.getDate() + 365);
      return nonExpiredEntries.filter(e => {
        const d = new Date(e.date);
        return d >= today && d <= next365Days;
      });
    }
    
    return nonExpiredEntries;
  }, [nonExpiredEntries, statsFilter]);

  // Alerts - Προθεσμίες που έχουν λήξει ή πλησιάζουν (επόμενες 3 ημέρες)
  const alerts = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const threeDaysLater = new Date(today);
    threeDaysLater.setDate(today.getDate() + 3);

    return entries
      .filter(e => {
        const d = new Date(e.date);
        d.setHours(0, 0, 0, 0);
        return d <= threeDaysLater;
      })
      .map(e => {
        const client = clients.find(c => c.id === e.client_id);
        const d = new Date(e.date);
        d.setHours(0, 0, 0, 0);
        const isExpired = d < today;
        const isToday = d.getTime() === today.getTime();
        return { ...e, client, isExpired, isToday };
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [entries, clients]);

  // Cases summary by client
  const casesByClient = useMemo(() => {
    const summary = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    cases.forEach(c => {
      const client = clients.find(cl => cl.id === c.client_id);
      if (client) {
        const clientName = removeAccents(client.client_type === 'legal' 
          ? client.company_name 
          : `${client.surname} ${client.name}`).toUpperCase();
        
        if (!summary[client.id]) {
          summary[client.id] = {
            clientName,
            clientId: client.id,
            cases: [],
            totalEntries: 0,
            nearestDeadline: null
          };
        }
        
        const caseEntries = entries.filter(e => e.case_id === c.id);
        summary[client.id].cases.push({
          ...c,
          entriesCount: caseEntries.length
        });
        summary[client.id].totalEntries += caseEntries.length;
        
        // Find nearest deadline for this client
        caseEntries.forEach(entry => {
          const entryDate = new Date(entry.date);
          entryDate.setHours(0, 0, 0, 0);
          if (entryDate >= today) {
            if (!summary[client.id].nearestDeadline || entryDate < summary[client.id].nearestDeadline) {
              summary[client.id].nearestDeadline = entryDate;
            }
          }
        });
      }
    });

    return Object.values(summary).sort((a, b) =>
      a.clientName.localeCompare(b.clientName, 'el', { sensitivity: 'base' })
    );
  }, [cases, clients, entries]);

  return (
    <div className="h-screen overflow-hidden bg-[#f7f2e7] max-sm:bg-[#f7f2e7] max-sm:min-h-screen max-sm:h-auto max-sm:overflow-auto flex flex-col">
      {/* Header */}
      <header className="backdrop-blur-md bg-white/90 border-b border-stone-300 max-sm:!border-stone-200/70 shadow-sm max-sm:!shadow-none flex-shrink-0 sticky top-0 z-[60] max-sm:!fixed max-sm:!top-0 max-sm:!left-0 max-sm:!right-0 max-sm:[padding-top:env(safe-area-inset-top,0px)]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 py-1.5 sm:py-3">
          {/* Row 1: Logo + (mobile) hamburger / (desktop) marquee */}
          <div className="flex items-center justify-between mb-0 sm:mb-3 relative">
            <div className="flex-shrink-0">
              <CourtPlanLogo size="sm" />
            </div>
            {/* Mobile portrait: Συνδρομή + Ρυθμίσεις + Αποσύνδεση στα δεξιά */}
            <div className="sm:hidden portrait-only flex items-center gap-2">
              {billing && (
                <button
                  data-testid="credits-badge-mobile"
                  type="button"
                  onClick={() => setIsSubscriptionOpen(true)}
                  className="flex items-center justify-center w-9 h-9 rounded-md border text-xs font-bold transition-all bg-white border-stone-300 text-slate-700"
                  title={billing.is_beta_tester ? 'Beta Tester — απεριόριστη χρήση' : 'Συνδρομή'}
                >
                  <Coins className="w-4 h-4" />
                </button>
              )}
              <button
                data-testid="logout-btn-mobile"
                onClick={handleLogout}
                className="flex items-center justify-center w-9 h-9 rounded-md border border-stone-300 bg-white text-slate-600 hover:bg-stone-50 hover:text-red-700 transition-all"
                title="Αποσύνδεση"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
            {/* Mobile landscape: Νέα εγγραφή next to logo - REMOVED, moved to row 2 */}
            <div className="sm:hidden landscape-only flex items-center gap-1.5">
            </div>
            {/* Desktop: κενός χώρος — οι ειδήσεις μεταφέρθηκαν σε δική τους λωρίδα */}
            <div className="flex-1 hidden sm:block" />
            {/* Desktop: Συνδρομή + Ρυθμίσεις + Αποσύνδεση στα δεξιά της 1ης γραμμής (δίπλα στο logo) */}
            <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
              {billing && (
                <button
                  data-testid="credits-badge"
                  type="button"
                  onClick={() => setIsSubscriptionOpen(true)}
                  className="flex items-center gap-1.5 px-2 h-9 rounded-md border text-sm font-medium cursor-pointer transition-all hover:shadow-sm hover:bg-stone-50 bg-white border-stone-300 text-slate-700"
                  title={billing.is_beta_tester ? 'Beta Tester — απεριόριστη χρήση' : 'Άνοιγμα σελίδας Συνδρομής'}
                >
                  <Coins className="w-4 h-4" />
                  {billing.is_beta_tester ? 'Beta' : (billing.unlimited_active ? '∞' : billing.credits)}
                </button>
              )}
              <button
                data-testid="law-guide-btn"
                onClick={() => navigate('/law-changes')}
                className="flex items-center justify-center h-9 w-9 border border-stone-300 rounded-md text-slate-600 bg-white hover:bg-stone-50 hover:text-red-700 transition-all"
                title="Οδηγός νομοθετικών αλλαγών"
              >
                <BookOpen className="w-4 h-4" />
              </button>
              <button
                data-testid="settings-btn"
                onClick={() => setIsSettingsOpen(true)}
                className="flex items-center justify-center h-9 w-9 border border-stone-300 rounded-md text-slate-600 bg-white hover:bg-stone-50 transition-all"
                title="Ρυθμίσεις"
              >
                <SettingsIcon className="w-4 h-4" />
              </button>
              <button
                data-testid="logout-btn"
                onClick={handleLogout}
                className="flex items-center justify-center h-9 w-9 border border-stone-300 rounded-md text-slate-600 bg-white hover:bg-stone-50 hover:text-red-700 transition-all"
                title="Αποσύνδεση"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Row 2: Mobile = Νέα εγγραφή / Row 3 = Χρονοχρέωση + Οικονομικά. Desktop = full nav */}
          {/* Mobile Row 2: REMOVED (moved to bottom tab bar + FAB + drawer) */}

          {/* Mobile Row 3 (landscape only) - Desktop nav unchanged */}
          <div className="sm:hidden flex items-center justify-center gap-2 mobile-nav">
            {/* Landscape: Χρονοχρέωση + Οικονομικά left, toggle center */}
            <div className="landscape-only flex items-center justify-between w-full relative">
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setIsTimeTrackerOpen(true)}
                  className={`flex items-center gap-1 px-2 h-7 rounded-md border text-[11px] font-medium transition-all ${
                    hasActiveTimer 
                      ? 'bg-green-600 text-white border-green-600 animate-pulse' 
                      : 'border-stone-300 bg-white text-slate-600'
                  }`}
                >
                  <Clock className="w-3 h-3" />
                  Χρονοχρέωση
                </button>
                {user?.office_role !== 'secretary' && (
                <button
                  onClick={() => setIsFinancialOpen(true)}
                  className="flex items-center gap-1 px-2 h-7 rounded-md border border-stone-300 bg-white text-slate-600 text-[11px] font-medium"
                >
                  <Wallet className="w-3 h-3" />
                  Οικονομικά
                </button>
                )}
              </div>
              <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-1.5">
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`flex items-center gap-1 px-2 h-7 rounded-md border text-[11px] font-bold transition-all shadow-sm ${
                    viewMode === 'calendar' 
                      ? 'bg-red-700 border-red-700 text-white' 
                      : 'bg-white border-stone-300 text-slate-600'
                  }`}
                >
                  <Calendar className="w-3 h-3" />
                  Ημερολόγιο
                </button>
                <button
                  onClick={() => setViewMode('dashboard')}
                  className={`flex items-center gap-1 px-2 h-7 rounded-md border text-[11px] font-bold transition-all shadow-sm ${
                    viewMode === 'dashboard' 
                      ? 'bg-red-700 border-red-700 text-white' 
                      : 'bg-white border-stone-300 text-slate-600'
                  }`}
                >
                  <LayoutDashboard className="w-3 h-3" />
                  Dashboard
                </button>
              </div>
              <Button
                onClick={() => setIsFormOpen(true)}
                className="h-7 px-2 bg-red-700 text-white hover:bg-red-800 shadow-sm font-medium text-[11px] rounded"
              >
                <Plus className="w-3 h-3 mr-0.5" />
                Νέα εγγραφή
              </Button>
            </div>
          </div>

          {/* Desktop Row 2 */}
          <div className="hidden sm:flex flex-wrap items-center justify-between gap-2 desktop-nav">
            {/* Left: Χρονοχρέωση + Οικονομικά */}
            <div className="flex items-center gap-2">
              <button
                data-testid="time-tracker-btn"
                onClick={() => setIsTimeTrackerOpen(true)}
                className={`flex items-center gap-2 px-4 h-10 rounded-lg border text-sm font-medium transition-all shadow-sm ${
                  hasActiveTimer 
                    ? 'bg-green-600 border-green-600 text-white animate-pulse' 
                    : 'bg-white border-stone-300 text-slate-600 hover:text-slate-900 hover:border-red-700/50'
                }`}
              >
                <Clock className="w-4 h-4" />
                <span className="flex items-center gap-1">
                  Χρονοχρέωση
                  {hasActiveTimer && <span className="text-xs">▶</span>}
                </span>
              </button>
              {user?.office_role !== 'secretary' && (
              <button
                data-testid="financial-btn"
                onClick={() => setIsFinancialOpen(true)}
                className="flex items-center gap-2 px-4 h-10 rounded-lg border border-stone-300 bg-white text-sm font-medium transition-all shadow-sm text-slate-600 hover:text-slate-900 hover:border-red-700/50"
              >
                <Wallet className="w-4 h-4" />
                Οικονομικά
              </button>
              )}
              {/* Ομάδα: κώδικας διατηρείται, το κουμπί κρυμμένο κατόπιν αιτήματος */}
              {false && (user?.office_role === 'head' || user?.is_admin) && (
              <button
                data-testid="office-team-btn"
                onClick={() => setIsTeamOpen(true)}
                className="flex items-center gap-2 px-4 h-10 rounded-lg border border-stone-300 bg-white text-sm font-medium transition-all text-slate-600 hover:text-slate-900"
              >
                <Users className="w-4 h-4" />
                Ομάδα
              </button>
              )}
            </div>

            {/* Center: View Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-2 px-4 h-10 rounded-lg border text-sm font-bold transition-all shadow-sm ${
                  viewMode === 'calendar' 
                    ? 'bg-red-700 border-red-700 text-white' 
                    : 'bg-white border-stone-300 text-slate-600 hover:text-slate-900 hover:border-red-700/50'
                }`}
              >
                <Calendar className="w-4 h-4" />
                Ημερολόγιο
              </button>
              <button
                onClick={() => setViewMode('dashboard')}
                className={`flex items-center gap-2 px-4 h-10 rounded-lg border text-sm font-bold transition-all shadow-sm ${
                  viewMode === 'dashboard' 
                    ? 'bg-red-700 border-red-700 text-white' 
                    : 'bg-white border-stone-300 text-slate-600 hover:text-slate-900 hover:border-red-700/50'
                }`}
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard
              </button>
            </div>

            {/* Right: Other buttons */}
            <div className="flex items-center gap-3">
              {user?.is_admin && (
                <button
                  data-testid="admin-nav-btn"
                  onClick={() => navigate('/admin')}
                  className="hidden sm:flex items-center gap-2 h-10 px-4 border border-stone-300 rounded-md text-slate-700 bg-white hover:bg-stone-50 transition-all font-medium text-sm"
                >
                  <Shield className="w-4 h-4" />
                  Admin
                </button>
              )}
              <Button
                data-testid="add-entry-btn"
                onClick={() => setIsFormOpen(true)}
                className="h-10 px-6 bg-red-700 text-white hover:bg-red-800 transition-all shadow-md hover:shadow-lg font-medium text-sm rounded-md"
              >
                <Plus className="w-4 h-4 mr-2" />
                Νέα εγγραφή
              </Button>
            </div>
          </div>
        </div>
        {/* Λωρίδα νομικών ειδήσεων — δική της γραμμή, κλείνει για την ημέρα */}
        {!newsDismissed && (
          <div className="hidden sm:block border-t border-stone-200 bg-white/70" data-testid="legal-news-strip">
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 h-8 flex items-center gap-3">
              <span className="text-[11px] font-bold text-red-700 shrink-0 tracking-widest">ΕΙΔΗΣΕΙΣ</span>
              <div className="flex-1 overflow-hidden">
                <div className="animate-marquee whitespace-nowrap" data-testid="legal-news-ticker">
                  <span className="text-slate-700 text-xs font-medium">
                    {legalNews.length === 0 ? (
                      <span className="text-slate-400">Φόρτωση νομικών νέων...</span>
                    ) : (
                      [0, 1].map((pass) => (
                        legalNews.map((n, i) => (
                          <React.Fragment key={`${pass}-${i}`}>
                            <a
                              href={n.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-red-700 hover:underline transition-colors"
                              title={n.source ? `${n.source}` : ''}
                            >
                              {n.title}
                            </a>
                            {n.source && <span className="text-slate-400 text-[10px] ml-1">({n.source})</span>}
                            <span className="mx-4 text-slate-300">•</span>
                          </React.Fragment>
                        ))
                      ))
                    )}
                  </span>
                </div>
              </div>
              <button
                onClick={dismissNews}
                data-testid="news-strip-close-btn"
                title="Απόκρυψη για σήμερα"
                className="shrink-0 w-5 h-5 flex items-center justify-center text-slate-400 hover:text-slate-700"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main ref={mainScrollRef} className="cp-mobile-main-pad flex-1 overflow-hidden max-sm:!overflow-auto max-w-[1400px] w-full mx-auto px-4 sm:px-6 md:px-12 max-sm:!px-0 py-1 sm:py-3 max-sm:!py-0 flex flex-col max-sm:pb-24 sm:pb-0 relative min-h-0">
        {effectiveViewMode === 'dashboard' ? (
          <>
            {/* Statistics Cards - hidden on mobile (replaced with bottom tabs + filter chip) */}
            <div className="hidden sm:block flex-shrink-0 pb-1.5 sm:pb-2">
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <button
                  onClick={() => {
                    setStatsFilter('today');
                    if (window.innerWidth < 640) setMobileExpanded('entries');
                  }}
                  className={`bg-white rounded-lg px-2.5 py-1.5 shadow-sm border-2 transition-all text-left ${
                    (statsFilter === 'today' && (mobileExpanded === 'entries' || window.innerWidth >= 640))
                      ? 'border-stone-500' 
                      : 'border-stone-200 hover:border-stone-400'
                  }`}
                >
                  <div className="flex items-center justify-between gap-1 sm:gap-2">
                    <div>
                      <p className="text-[10px] sm:text-xs text-slate-500 leading-tight">Σήμερα</p>
                      <p className="text-base sm:text-lg font-bold text-slate-900 leading-tight">{stats.todayCount}</p>
                    </div>
                    <div className="w-7 h-7 sm:w-7 sm:h-7 bg-stone-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-500" />
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setStatsFilter('week');
                    if (window.innerWidth < 640) setMobileExpanded('entries');
                  }}
                  className={`bg-white rounded-lg px-2.5 py-1.5 shadow-sm border-2 transition-all text-left ${
                    (statsFilter === 'week' && (mobileExpanded === 'entries' || window.innerWidth >= 640))
                      ? 'border-stone-500' 
                      : 'border-stone-200 hover:border-stone-400'
                  }`}
                >
                  <div className="flex items-center justify-between gap-1 sm:gap-2">
                    <div>
                      <p className="text-[10px] sm:text-xs text-slate-500 leading-tight">7 ημέρες</p>
                      <p className="text-base sm:text-lg font-bold text-slate-900 leading-tight">{stats.weekCount}</p>
                    </div>
                    <div className="w-7 h-7 sm:w-7 sm:h-7 bg-stone-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-500" />
                    </div>
                  </div>
                </button>

                <button
                  data-testid="stats-third-btn"
                  onClick={() => {
                    // Mobile: switch to clients accordion. Desktop: toggle the 30-day filter.
                    if (window.innerWidth < 640) {
                      // Show clients section, hide entries
                      setMobileExpanded((cur) => (cur === 'clients' ? 'entries' : 'clients'));
                      // Reset client/case selection so the FULL clients list shows
                      setSelectedClientId(null);
                      setSelectedCaseId(null);
                    } else {
                      setStatsFilter(statsFilter === 'month' ? null : 'month');
                    }
                  }}
                  className={`bg-white rounded-lg px-2.5 py-1.5 shadow-sm border-2 transition-all text-left ${
                    (mobileExpanded === 'clients' || statsFilter === 'month')
                      ? 'border-stone-500'
                      : 'border-stone-200 hover:border-stone-400'
                  }`}
                >
                  <div className="flex items-center justify-between gap-1 sm:gap-2">
                    <div>
                      {/* Mobile: "Πελάτες" label, no count. Desktop: keep "30 ημέρες" + count. */}
                      <p className="text-[10px] sm:text-xs text-slate-500 leading-tight">
                        <span className="sm:hidden">Πελάτες</span>
                        <span className="hidden sm:inline">30 ημέρες</span>
                      </p>
                      <p className="text-base sm:text-lg font-bold text-slate-900 leading-tight">
                        <span className="hidden sm:inline">{stats.monthCount}</span>
                        <span className="sm:hidden">&nbsp;</span>
                      </p>
                    </div>
                    <div className="w-7 h-7 sm:w-7 sm:h-7 bg-stone-100 rounded-full flex items-center justify-center flex-shrink-0">
                      {/* Mobile: Users icon. Desktop: CalendarDays icon. */}
                      <Users className="w-3.5 h-3.5 sm:hidden text-slate-500" />
                      <CalendarDays className="hidden sm:block w-4 h-4 text-slate-500" />
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Mobile Horizontal Segmented Control: Σήμερα / 7 ημέρες / 30 ημέρες
                Visible only on mobile, in 'entries' or 'planned' tabs.
                FIXED right below the logo header — never moves. */}
            {mobileExpanded === 'entries' && !isAnyMobileModalOpen && (
              <div className="sm:hidden cp-mobile-toolbar-fixed" data-testid="mobile-period-segmented">
                <div className="flex w-full gap-1.5">
                  {[
                    { key: 'today', label: 'Σήμερα' },
                    { key: 'week', label: '7 ημέρες' },
                    { key: 'month', label: '30 ημέρες' },
                  ].map((opt) => {
                    const active = statsFilter === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        data-testid={`segmented-period-${opt.key}`}
                        onClick={() => { setStatsFilter(opt.key); setSelectedDate(null); }}
                        className={`flex-1 text-xs font-semibold py-1.5 rounded-full border transition-all ${
                          active
                            ? 'bg-red-700 border-red-700 text-white shadow-sm'
                            : 'bg-white border-stone-300 text-slate-600'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Mobile Clients Tab Fixed Toolbar — under logo header */}
            {mobileExpanded === 'clients' && !isAnyMobileModalOpen && (
              <div className="sm:hidden cp-mobile-toolbar-fixed" data-testid="mobile-client-toolbar">
                {(selectedClientId || selectedCaseId) ? (
                  /* Breadcrumb / Επιστροφή when a client or case is selected — styled like the "Κλείσιμο" dialog button */
                  <button
                    onClick={() => {
                      if (selectedCaseId) {
                        setSelectedCaseId(null);
                      } else {
                        setSelectedClientId(null);
                      }
                    }}
                    className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 bg-stone-100 hover:bg-slate-200 hover:text-slate-900 transition-colors leading-tight"
                    data-testid="mobile-back-to-clients"
                  >
                    <span className="text-base leading-none">‹</span>
                    Επιστροφή
                  </button>
                ) : (
                  <div className="relative w-full">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Αναζήτηση πελάτη..."
                      value={clientSearch}
                      onChange={(e) => {
                        setClientSearch(e.target.value);
                        if (e.target.value && (selectedClientId || selectedCaseId)) {
                          setSelectedClientId(null);
                          setSelectedCaseId(null);
                        }
                      }}
                      onFocus={() => {
                        if (selectedClientId || selectedCaseId) {
                          setSelectedClientId(null);
                          setSelectedCaseId(null);
                        }
                      }}
                      className="w-full pl-9 pr-9 py-2 border border-stone-300 rounded-full text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent"
                      autoComplete="off"
                      data-lpignore="true"
                    />
                    {clientSearch && (
                      <button
                        onClick={() => setClientSearch('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Dashboard Grid - 3 Columns - Takes remaining height */}
            <div className="flex-1 min-h-0 max-sm:!overflow-hidden max-sm:!flex-1 max-sm:!flex max-sm:!flex-col grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 overflow-hidden">
              {/* Column 3 (mobile 1st): Entries List - All Entries */}
              <div className={`lg:col-span-1 lg:h-full lg:min-h-0 order-1 lg:order-3 max-sm:!flex max-sm:!flex-col max-sm:!flex-1 max-sm:!min-h-0 ${mobileExpanded === 'entries' ? '' : 'hidden lg:block'}`} data-testid="entries-list-section">
                {/* Mobile shows the list directly when mobileExpanded === 'entries' (no accordion header). */}
                <div className={`${mobileExpanded === 'entries' ? 'block max-sm:!flex max-sm:!flex-col max-sm:!flex-1 max-sm:!min-h-0' : 'hidden'} lg:block lg:h-full lg:min-h-0`}>
                  {loading ? (
                    <div className="bg-white rounded-2xl border border-stone-200/60 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
                      <div className="p-4 border-b border-stone-100">
                        <div className="h-4 w-24 bg-slate-200 rounded animate-pulse" />
                      </div>
                      <EntriesSkeleton count={4} />
                    </div>
                  ) : (
                  <EntriesList
                    entries={filteredEntries}
                    allEntries={nonExpiredEntries}
                    clients={clients}
                    cases={cases}
                    onDelete={handleDeleteEntry}
                    selectedDate={selectedDate}
                    onClearFilter={() => {
                      setSelectedDate(null);
                      setStatsFilter(null);
                    }}
                    onChangeStatsFilter={(next) => {
                      setStatsFilter(next);
                      setSelectedDate(null);
                    }}
                    onEntryClick={handleEntryClick}
                    statsFilter={statsFilter}
                    onRefresh={fetchData}
                    onOpenNewEntry={() => setIsFormOpen(true)}
                  />
                  )}
                </div>
              </div>

              {/* Column 1 (mobile 3rd): Πελάτες/Υποθέσεις */}
              <div className={`lg:col-span-1 lg:h-full lg:min-h-0 order-3 lg:order-1 max-sm:!flex max-sm:!flex-col max-sm:!flex-1 max-sm:!min-h-0 ${mobileExpanded === 'clients' ? '' : 'hidden lg:block'}`}>
                {/* Mobile: NO accordion header (controlled by the 3rd stats card "Πελάτες"). */}
                <div className={`${mobileExpanded === 'clients' ? 'block cp-mobile-fill-toolbar' : 'hidden'} lg:flex lg:h-full bg-white rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04)] border border-stone-200/60 flex flex-col overflow-hidden lg:min-h-0`}>
                <div className="p-3 sm:p-4 max-sm:!px-4 max-sm:!pt-2 max-sm:!pb-2 border-b border-stone-100 max-sm:!border-b-0 flex-shrink-0 cp-panel-sticky-header rounded-t-2xl bg-white">
                  <div className="flex items-center justify-between max-sm:hidden">
                    <h2 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center gap-2">
                      {selectedCaseId ? (
                        <>
                          <FolderOpen className="hidden sm:block w-4 h-4 sm:w-5 sm:h-5 text-slate-500" />
                          Φάκελος υπόθεσης
                        </>
                      ) : selectedClientId ? (
                        <>
                          <Briefcase className="hidden sm:block w-5 h-5 text-slate-500" />
                          Υποθέσεις πελάτη
                        </>
                      ) : (
                        <>
                          <Users className="hidden sm:block w-5 h-5 text-slate-500" />
                          Πελάτες
                        </>
                      )}
                    </h2>
                    {(selectedClientId || selectedCaseId) && (
                      <button
                        onClick={() => {
                          if (selectedCaseId) {
                            setSelectedCaseId(null);
                          } else {
                            setSelectedClientId(null);
                          }
                        }}
                        className="text-sm text-red-700 hover:text-red-800 transition-all"
                      >
                        Επιστροφή
                      </button>
                    )}
                  </div>
                  <p className="hidden sm:block text-sm text-slate-500">
                    {selectedCaseId 
                      ? 'Προθεσμίες και εργασίες της επιλεγμένης υπόθεσης'
                      : selectedClientId 
                        ? `${casesByClient.find(c => c.clientId === selectedClientId)?.clientName || ''}`
                        : 'Επιλέξτε πελάτη για να δείτε τις υποθέσεις του'
                    }
                  </p>
                  {/* Client Search - desktop only; mobile uses a fixed toolbar below logo (rendered separately) */}
                  <div className="relative mt-3 hidden sm:block">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Αναζήτηση πελάτη..."
                      value={clientSearch}
                      onChange={(e) => {
                        setClientSearch(e.target.value);
                        // Reset client/case selection when user starts searching
                        if (e.target.value && (selectedClientId || selectedCaseId)) {
                          setSelectedClientId(null);
                          setSelectedCaseId(null);
                        }
                      }}
                      onFocus={() => {
                        // When focusing search, return to clients list
                        if (selectedClientId || selectedCaseId) {
                          setSelectedClientId(null);
                          setSelectedCaseId(null);
                        }
                      }}
                      className="w-full pl-9 pr-9 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent"
                      autoComplete="off"
                      data-lpignore="true"
                    />
                    {clientSearch && (
                      <button
                        onClick={() => setClientSearch('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="p-4 flex-1 min-h-0 overflow-y-auto">
                  {!selectedClientId && !selectedCaseId ? (
                    // Show Clients List
                    (() => {
                      const filteredClients = clientSearch 
                        ? casesByClient.filter(item => 
                            item.clientName.toLowerCase().includes(clientSearch.toLowerCase())
                          )
                        : casesByClient;
                      
                      if (filteredClients.length === 0) {
                        return <EmptyState testId="clients-empty-state" icon={Users} compact text={clientSearch ? 'Δεν βρέθηκαν πελάτες' : 'Δεν υπάρχουν πελάτες'} />;
                      }
                      
                      return (
                        <div className="space-y-2">
                          {filteredClients.map(item => {
                            const clientObj = clients.find(c => c.id === item.clientId);
                            return (
                            <div
                              key={item.clientId}
                              onClick={() => setSelectedClientId(item.clientId)}
                              className="w-full p-3 rounded-xl border border-stone-200/60 bg-white hover:bg-stone-50 hover:border-stone-300 transition-all text-left cursor-pointer shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                            >
                              {/* Όνομα & Επεξεργασία */}
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm text-slate-900">
                                  {item.clientName}
                                </span>
                                <button
                                  onClick={(e) => handleEditClient(clientObj, e)}
                                  className="text-xs text-slate-500 hover:text-red-700 transition-colors"
                                >
                                  Επεξεργασία
                                </button>
                              </div>
                              {/* Αριθμός υποθέσεων & Λήξη πλησιέστερης */}
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-xs text-slate-600">
                                  {item.cases.length} {item.cases.length === 1 ? 'υπόθεση' : 'υποθέσεις'}
                                </span>
                                {item.nearestDeadline && (
                                  <span className="text-xs text-amber-600">
                                    {(() => {
                                      const today = new Date();
                                      today.setHours(0, 0, 0, 0);
                                      const diffDays = Math.ceil((item.nearestDeadline - today) / (1000 * 60 * 60 * 24));
                                      if (diffDays === 0) return 'Προθεσμία που λήγει σήμερα';
                                      if (diffDays === 1) return 'Προθεσμία που λήγει σε 1 ημέρα';
                                      return `Προθεσμία που λήγει σε ${diffDays} ημέρες`;
                                    })()}
                                  </span>
                                )}
                              </div>
                            </div>
                          );})}
                        </div>
                      );
                    })()
                  ) : selectedClientId && !selectedCaseId ? (
                    // Show Cases for Selected Client with Case Details
                    (() => {
                      const clientData = casesByClient.find(c => c.clientId === selectedClientId);
                      if (!clientData || clientData.cases.length === 0) {
                        return <p className="text-slate-500 text-sm text-center py-4">Δεν υπάρχουν υποθέσεις για αυτόν τον πελάτη</p>;
                      }
                      
                      // Helper to get case description label
                      const getCaseDescLabel = (caseItem) => {
                        // Find an entry with case description
                        const caseEntry = entries.find(e => e.case_id === caseItem.id && e.case_description_type);
                        if (caseEntry) {
                          if (caseEntry.case_description_type === 'other' && caseEntry.case_description_custom) {
                            return caseEntry.case_description_custom;
                          }
                          return CASE_DESCRIPTION_LABELS[caseEntry.case_description_type] || null;
                        }
                        return null;
                      };
                      
                      // Helper to get opponent details
                      const getOpponentDetails = (caseItem) => {
                        const caseEntry = entries.find(e => e.case_id === caseItem.id && e.opponent_details);
                        return caseEntry?.opponent_details || null;
                      };
                      
                      // Helper to open case modal
                      const openCaseModal = (caseItem) => {
                        const caseEntries = entries.filter(e => e.case_id === caseItem.id);
                        if (caseEntries.length > 0) {
                          // Set selected entry to first entry of this case to open the modal
                          setSelectedEntry(caseEntries[0]);
                        }
                      };
                      
                      // Get client name
                      const selectedClient = clients.find(c => c.id === selectedClientId);
                      const clientName = selectedClient 
                        ? removeAccents(selectedClient.client_type === 'natural' 
                            ? `${selectedClient.surname} ${selectedClient.name}` 
                            : selectedClient.company_name).toUpperCase()
                        : '';
                      
                      // Helper to get nearest deadline for a case
                      const getNearestDeadline = (caseItem) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const caseEntries = entries.filter(e => e.case_id === caseItem.id);
                        let nearest = null;
                        caseEntries.forEach(entry => {
                          const entryDate = new Date(entry.date);
                          entryDate.setHours(0, 0, 0, 0);
                          if (entryDate >= today) {
                            if (!nearest || entryDate < nearest) {
                              nearest = entryDate;
                            }
                          }
                        });
                        return nearest;
                      };
                      
                      return (
                        <div className="space-y-3">
                          {clientData.cases.map(caseItem => {
                            const caseDescLabel = getCaseDescLabel(caseItem);
                            const opponentDetails = getOpponentDetails(caseItem);
                            const nearestDeadline = getNearestDeadline(caseItem);
                            
                            return (
                              <div
                                key={caseItem.id}
                                onClick={() => caseItem.entriesCount > 0 && openCaseModal(caseItem)}
                                className={`p-3 rounded-xl border transition-all border-stone-200/60 bg-white hover:bg-stone-50 hover:border-stone-300 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${caseItem.entriesCount > 0 ? 'cursor-pointer' : ''}`}
                                data-testid={`case-card-${caseItem.id}`}
                              >
                                {/* Όνομα πελάτη + Delete button (only when no entries) */}
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <p className="font-bold text-sm text-slate-900">
                                    {clientName}
                                  </p>
                                  {caseItem.entriesCount === 0 && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (!window.confirm('Διαγραφή της υπόθεσης; (Δεν έχει εγγραφές)')) return;
                                        try {
                                          await axios.delete(`${API}/cases/${caseItem.id}`);
                                          toast.success('Η υπόθεση διαγράφηκε');
                                          fetchData();
                                        } catch (err) {
                                          toast.error(err.response?.data?.detail || 'Σφάλμα διαγραφής');
                                        }
                                      }}
                                      className="p-1 rounded hover:bg-red-50 text-red-600 hover:text-red-700 transition-colors flex-shrink-0"
                                      title="Διαγραφή υπόθεσης"
                                      data-testid={`delete-empty-case-${caseItem.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                                
                                {/* Περιγραφή υπόθεσης & Αντίδικος - same format as EntriesList */}
                                {(caseDescLabel || opponentDetails) && (
                                  <div className="mb-1 space-y-0.5">
                                    {caseDescLabel && (
                                      <p className="text-xs text-slate-600">{caseDescLabel}</p>
                                    )}
                                    {opponentDetails && (
                                      <p className="text-xs text-slate-600">Αντίδικος: {opponentDetails}</p>
                                    )}
                                  </div>
                                )}
                                
                                {/* Λήξη πλησιέστερης εγγραφής & Αριθμός εγγραφών */}
                                <div className="flex items-center justify-between text-xs mt-2">
                                  {nearestDeadline ? (
                                    <span className="text-amber-600">
                                      {(() => {
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        const diffDays = Math.ceil((nearestDeadline - today) / (1000 * 60 * 60 * 24));
                                        if (diffDays === 0) return 'Προθεσμία που λήγει σήμερα';
                                        if (diffDays === 1) return 'Προθεσμία που λήγει σε 1 ημέρα';
                                        return `Προθεσμία που λήγει σε ${diffDays} ημέρες`;
                                      })()}
                                    </span>
                                  ) : (
                                    <span className="text-slate-500">{caseItem.court || ''}</span>
                                  )}
                                  <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">
                                    {caseItem.entriesCount} εγγραφές
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()
                  ) : (
                    // Case selected - redirect back to cases list (the modal will open automatically)
                    (() => {
                      // Reset selectedCaseId and show cases list again
                      // The modal opening is handled by openCaseModal in the previous section
                      setSelectedCaseId(null);
                      return null;
                    })()
                  )}
                </div>
              </div>
              </div>

              {/* Column 2 (mobile: visible only when Προγραμματισμός tab is active): Προγραμματισμένες ενέργειες */}
              <div className={`lg:col-span-1 lg:h-full lg:min-h-0 order-2 lg:order-2 max-sm:!flex max-sm:!flex-col max-sm:!flex-1 max-sm:!min-h-0 ${mobileExpanded === 'planned' ? '' : 'hidden lg:block'}`} data-testid="planned-section">
                {/* Mobile-only accordion header (will not render because parent is hidden on mobile) */}
                <button
                  type="button"
                  onClick={() => toggleMobile('planned')}
                  className="hidden w-full items-center justify-between px-3 py-2.5 bg-white rounded-lg shadow-sm border border-stone-200 mb-2"
                  data-testid="mobile-toggle-planned"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    Προγραμματισμένες ενέργειες
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${mobileExpanded === 'planned' ? 'rotate-180' : ''}`} />
                </button>
                <div className={`${mobileExpanded === 'planned' ? 'block cp-mobile-fill' : 'hidden'} lg:block lg:h-full bg-white rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04)] border border-stone-200/60 overflow-y-auto lg:min-h-0 p-3 sm:p-4`} data-testid="embedded-day-planner">
                  <DayPlanner embedded />
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Calendar View */
          <div ref={calendarRef} className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            <div className="lg:col-span-2" ref={calendarPanelRef}>
              <CalendarView
                entries={nonExpiredEntries}
                clients={clients}
                cases={cases}
                selectedDate={selectedDate}
                onDateSelect={(date) => {
                  if (!date) { setSelectedDate(null); return; }
                  const ymd = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                  navigate(`/day-planner?date=${ymd}`);
                }}
              />
            </div>
            <div className="lg:col-span-1" data-testid="entries-list-section" style={{ '--cal-h': calPanelHeight ? `${calPanelHeight}px` : '688px' }}>
              <EntriesList
                entries={nonExpiredEntries}
                allEntries={nonExpiredEntries}
                clients={clients}
                cases={cases}
                onDelete={handleDeleteEntry}
                selectedDate={selectedDate}
                onClearFilter={() => setSelectedDate(null)}
                onEntryClick={handleEntryClick}
                onRefresh={fetchData}
                matchCalendarHeight={true}
                onOpenNewEntry={() => setIsFormOpen(true)}
              />
            </div>
          </div>
        )}
      </main>

      {/* Entry Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-[683px] max-h-[90vh] overflow-y-auto sm:rounded-2xl" aria-describedby={undefined} data-testid="entry-form-dialog">
          <DialogHeader className="space-y-1">
            <DialogTitle className="text-base sm:text-xl font-semibold text-slate-900 flex items-center gap-2.5">
              <span className="hidden sm:flex items-center justify-center w-8 h-8 rounded-lg bg-red-700/10 text-red-700 shrink-0">
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              </span>
              Νέα εγγραφή
            </DialogTitle>
            <p className="hidden sm:block text-sm text-slate-500">Δικαστική πράξη ή άλλη απασχόληση — οι προθεσμίες υπολογίζονται αυτόματα</p>
          </DialogHeader>
          <EntryWizard
            onComplete={handleFormComplete}
            onCancel={() => { setIsFormOpen(false); setEntryPrefillData(null); }}
            onOpenCaseModal={handleOpenCaseModal}
            prefillData={entryPrefillData}
          />
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={isSettingsOpen} onOpenChange={(open) => {
        setIsSettingsOpen(open);
        // When closing the Settings dialog, refresh data so any cleared entries disappear immediately
        if (!open) {
          fetchData();
          setSettingsInitialTab('profile'); // reset for next open
        }
      }}>
        <DialogContent className="sm:max-w-[800px] !top-[64px] sm:!top-[140px] !translate-y-0 max-h-[calc(100vh-80px)] sm:max-h-[calc(100vh-160px)] p-0 overflow-hidden flex flex-col">
          <button
            onClick={() => setIsSettingsOpen(false)}
            className="absolute right-3 top-3 z-50 rounded-full p-1.5 bg-stone-100 hover:bg-slate-200 transition-all focus:outline-none"
          >
            <X className="h-4 w-4 text-slate-600" />
            <span className="sr-only">Close</span>
          </button>
          <div className="overflow-y-auto flex-1 min-h-0" style={{ scrollbarGutter: 'stable' }}>
            <Settings onDataChanged={fetchData} initialTab={settingsInitialTab} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobile Bottom Tab Bar - portrait only */}
      <div className="sm:hidden portrait-only fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-stone-200/70 grid grid-cols-4 z-[60]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0.5rem)', paddingTop: '0.5rem', boxShadow: '0 -1px 0 rgba(15,23,42,0.04)' }}>
        <button
          data-testid="tab-entries-mobile"
          onClick={() => { setViewMode('dashboard'); setMobileExpanded('entries'); setSelectedClientId(null); setSelectedCaseId(null); }}
          className={`flex flex-col items-center gap-0.5 py-1 transition-colors ${viewMode === 'dashboard' && mobileExpanded === 'entries' && !isFinancialSheetOpen && !isFinancialOpen && !isTimeTrackerOpen ? 'text-red-700' : 'text-slate-500'}`}
        >
          <FileText className="w-5 h-5" strokeWidth={viewMode === 'dashboard' && mobileExpanded === 'entries' && !isFinancialSheetOpen && !isFinancialOpen && !isTimeTrackerOpen ? 2.5 : 2} />
          <span className={`text-[10px] ${viewMode === 'dashboard' && mobileExpanded === 'entries' && !isFinancialSheetOpen && !isFinancialOpen && !isTimeTrackerOpen ? 'font-bold' : 'font-medium'}`}>Προθεσμίες</span>
        </button>
        <button
          data-testid="tab-planned-mobile"
          onClick={() => { setViewMode('dashboard'); setMobileExpanded('planned'); setSelectedClientId(null); setSelectedCaseId(null); }}
          className={`flex flex-col items-center gap-0.5 py-1 transition-colors ${viewMode === 'dashboard' && mobileExpanded === 'planned' && !isFinancialSheetOpen && !isFinancialOpen && !isTimeTrackerOpen ? 'text-red-700' : 'text-slate-500'}`}
        >
          <CalendarClock className="w-5 h-5" strokeWidth={viewMode === 'dashboard' && mobileExpanded === 'planned' && !isFinancialSheetOpen && !isFinancialOpen && !isTimeTrackerOpen ? 2.5 : 2} />
          <span className={`text-[10px] ${viewMode === 'dashboard' && mobileExpanded === 'planned' ? 'font-bold' : 'font-medium'}`}>Πρόγραμμα</span>
        </button>
        <button
          data-testid="tab-clients-mobile"
          onClick={() => { setViewMode('dashboard'); setMobileExpanded('clients'); setSelectedClientId(null); setSelectedCaseId(null); }}
          className={`flex flex-col items-center gap-0.5 py-1 transition-colors ${viewMode === 'dashboard' && mobileExpanded === 'clients' && !isFinancialSheetOpen && !isFinancialOpen && !isTimeTrackerOpen ? 'text-red-700' : 'text-slate-500'}`}
        >
          <Users className="w-5 h-5" strokeWidth={viewMode === 'dashboard' && mobileExpanded === 'clients' && !isFinancialSheetOpen && !isFinancialOpen && !isTimeTrackerOpen ? 2.5 : 2} />
          <span className={`text-[10px] ${viewMode === 'dashboard' && mobileExpanded === 'clients' && !isFinancialSheetOpen && !isFinancialOpen && !isTimeTrackerOpen ? 'font-bold' : 'font-medium'}`}>Πελάτες</span>
        </button>
        <button
          data-testid="tab-financial-mobile"
          onClick={() => setIsFinancialSheetOpen(true)}
          className={`flex flex-col items-center gap-0.5 py-1 transition-colors ${isFinancialSheetOpen || isFinancialOpen || isTimeTrackerOpen ? 'text-red-700' : (hasActiveTimer ? 'text-green-600' : 'text-slate-500')}`}
        >
          <Wallet className="w-5 h-5" strokeWidth={isFinancialSheetOpen || isFinancialOpen || isTimeTrackerOpen ? 2.5 : 2} />
          <span className={`text-[10px] ${isFinancialSheetOpen || isFinancialOpen || isTimeTrackerOpen ? 'font-bold' : 'font-medium'}`}>Οικονομικά</span>
        </button>
      </div>

      {/* Mobile Οικονομικά action sheet — picks between Time Tracking and Financial Management */}
      <Sheet open={isFinancialSheetOpen} onOpenChange={setIsFinancialSheetOpen}>
        <SheetContent
          side="bottom"
          className="sm:hidden portrait-only rounded-t-2xl p-0 border-0 !z-[80] [&>button]:hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          data-testid="financial-sheet"
        >
          <div className="px-5 pt-3 pb-2">
            <div className="mx-auto h-1 w-10 rounded-full bg-slate-300 mb-3" />
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-slate-900">Οικονομικά</h3>
              <button
                onClick={() => setIsFinancialSheetOpen(false)}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                data-testid="financial-sheet-close-btn"
              >
                Κλείσιμο
              </button>
            </div>
          </div>
          <div className="px-4 pb-5 pt-2 grid grid-cols-1 gap-2">
            <button
              data-testid="financial-sheet-timetrack-btn"
              onClick={() => { setIsFinancialSheetOpen(false); setIsTimeTrackerOpen(true); }}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-stone-200 bg-white hover:bg-stone-50 active:scale-[0.99] transition-all text-left"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasActiveTimer ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-700'}`}>
                <Clock className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">Χρονοχρέωση</div>
                <div className="text-xs text-slate-500">Καταγραφή χρόνου εργασίας</div>
              </div>
              {hasActiveTimer && (
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              )}
            </button>
            <button
              data-testid="financial-sheet-finmgmt-btn"
              onClick={() => { setIsFinancialSheetOpen(false); setIsFinancialOpen(true); }}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-stone-200 bg-white hover:bg-stone-50 active:scale-[0.99] transition-all text-left"
            >
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-700 flex items-center justify-center">
                <Wallet className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">Οικονομική διαχείριση</div>
                <div className="text-xs text-slate-500">Έσοδα, έξοδα, αμοιβές</div>
              </div>
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Mobile FAB - Νέα εγγραφή - portrait only */}
      <button
        data-testid="fab-new-entry-mobile"
        onClick={() => setIsFormOpen(true)}
        className="sm:hidden portrait-only fixed right-4 w-14 h-14 rounded-full bg-red-700 text-white flex items-center justify-center transition-transform active:scale-95 hover:bg-red-800 z-30"
        style={{ boxShadow: '0 8px 24px rgba(185, 28, 28, 0.4)', bottom: 'calc(env(safe-area-inset-bottom, 0.5rem) + 3.25rem)' }}
        aria-label="Νέα εγγραφή"
      >
        <Plus className="w-7 h-7" strokeWidth={3} />
      </button>

      {/* Mobile Hamburger Drawer */}
      <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <SheetContent side="right" className="w-[80vw] max-w-[320px] p-0 flex flex-col">
          <SheetHeader className="px-5 py-4 border-b border-stone-200 flex-shrink-0">
            <SheetTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Menu className="w-4 h-4" />
              Μενού
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto py-2">
            {billing && (
              <button
                onClick={() => { setIsMobileMenuOpen(false); setIsSubscriptionOpen(true); }}
                className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-stone-50 transition-colors"
              >
                <Coins className={`w-5 h-5 ${billing.unlimited_active ? 'text-green-600' : billing.credits < 10 ? 'text-amber-600' : 'text-slate-600'}`} />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-900">Συνδρομή</div>
                  <div className="text-xs text-slate-500">
                    {billing.unlimited_active ? 'Απεριόριστες μονάδες' : `${billing.credits} μονάδες`}
                  </div>
                </div>
              </button>
            )}
            <button
              onClick={() => { setIsMobileMenuOpen(false); setIsSettingsOpen(true); }}
              className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-stone-50 transition-colors"
            >
              <SettingsIcon className="w-5 h-5 text-slate-600" />
              <div className="text-sm font-semibold text-slate-900">Ρυθμίσεις</div>
            </button>
            {(user?.is_admin || user?.role === 'admin') && (
              <button
                onClick={() => { setIsMobileMenuOpen(false); navigate('/admin'); }}
                className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-stone-50 transition-colors"
              >
                <Shield className="w-5 h-5 text-red-700" />
                <div className="text-sm font-semibold text-slate-900">Admin Panel</div>
              </button>
            )}
            <div className="border-t border-stone-200 my-2"></div>
            <button
              onClick={() => { setIsMobileMenuOpen(false); handleLogout(); }}
              className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-5 h-5 text-red-700" />
              <div className="text-sm font-semibold text-red-700">Αποσύνδεση</div>
            </button>
          </div>
          {user && (
            <div className="px-5 py-3 border-t border-stone-200 bg-stone-50 flex-shrink-0">
              <div className="text-xs text-slate-500">Συνδεδεμένος ως</div>
              <div className="text-sm font-semibold text-slate-900 truncate">{user.email}</div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Subscription Dialog - opens directly like other modals */}
      <Dialog open={isSubscriptionOpen} onOpenChange={(open) => {
        setIsSubscriptionOpen(open);
        if (!open) fetchBilling();
      }}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto [&>[data-testid=dialog-close-btn]]:!bg-transparent [&>[data-testid=dialog-close-btn]]:!shadow-none [&>[data-testid=dialog-close-btn]]:!border-0 [&>[data-testid=dialog-close-btn]]:!ring-0 [&>[data-testid=dialog-close-btn]]:!ring-offset-0 [&>[data-testid=dialog-close-btn]]:hover:!bg-transparent [&>[data-testid=dialog-close-btn]]:focus:!ring-0 [&>[data-testid=dialog-close-btn]]:!outline-none" data-testid="subscription-dialog">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-xl font-semibold text-slate-900 flex items-center gap-2">
              <Coins className="hidden sm:block w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              Συνδρομή
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <SubscriptionTab />
          </div>
        </DialogContent>
      </Dialog>

      {/* Entry Detail Modal */}
      {selectedEntry && (
        <EntryDetail
          {...getEntryDetails()}
          onClose={() => setSelectedEntry(null)}
          onRefresh={fetchData}
          onOpenNewEntryWithData={handleOpenNewEntryWithData}
        />
      )}

      {/* Edit Client Modal */}
      <Dialog open={!!editingClient} onOpenChange={(open) => !open && setEditingClient(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-xl font-semibold text-slate-900 flex items-center gap-2">
              <User className="hidden sm:block w-4 h-4 sm:w-5 sm:h-5 text-slate-700" />
              Επεξεργασία στοιχείων πελάτη
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {/* Name fields */}
            <div>
              <Label htmlFor="edit-surname" className="text-base font-medium">{editClientForm.client_type === 'natural' ? 'Επώνυμο *' : 'Επωνυμία *'}</Label>
              <Input
                id="edit-surname"
                value={editClientForm.client_type === 'natural' ? editClientForm.surname : editClientForm.company_name}
                onChange={(e) => setEditClientForm({
                  ...editClientForm, 
                  [editClientForm.client_type === 'natural' ? 'surname' : 'company_name']: e.target.value
                })}
                placeholder="Υποχρεωτικό πεδίο"
                className="h-12 bg-white border-stone-300 !text-base"
                autoComplete="new-password"
                data-lpignore="true"
                data-form-type="other"
              />
            </div>
            <div className="mt-4">
              <Label htmlFor="edit-name" className="text-base font-medium">{editClientForm.client_type === 'natural' ? 'Όνομα *' : 'Διακριτικός τίτλος'}</Label>
              <Input
                id="edit-name"
                value={editClientForm.client_type === 'natural' ? editClientForm.name : ''}
                onChange={(e) => setEditClientForm({...editClientForm, name: e.target.value})}
                className="h-12 bg-white border-stone-300 !text-base"
                disabled={editClientForm.client_type === 'legal'}
                placeholder={editClientForm.client_type === 'legal' ? '(προαιρετικό)' : 'Υποχρεωτικό πεδίο'}
                autoComplete="new-password"
                data-lpignore="true"
                data-form-type="other"
              />
            </div>

            <div>
              <Label htmlFor="edit-vat" className="text-base font-medium">ΑΦΜ</Label>
              <Input
                id="edit-vat"
                value={editClientForm.vat}
                onChange={(e) => setEditClientForm({...editClientForm, vat: e.target.value})}
                className="h-12 bg-white border-stone-300 !text-base"
                autoComplete="new-password"
                data-lpignore="true"
                data-form-type="other"
              />
            </div>

            <div>
              <Label htmlFor="edit-phone" className="text-base font-medium">Κινητό επικοινωνίας</Label>
              <Input
                id="edit-phone"
                value={editClientForm.phone}
                onChange={(e) => setEditClientForm({...editClientForm, phone: e.target.value})}
                className="h-12 bg-white border-stone-300 !text-base"
                autoComplete="new-password"
                data-lpignore="true"
                data-form-type="other"
              />
            </div>

            <div>
              <Label htmlFor="edit-email" className="text-base font-medium">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editClientForm.email}
                onChange={(e) => setEditClientForm({...editClientForm, email: e.target.value})}
                className="h-12 bg-white border-stone-300 !text-base"
                autoComplete="new-password"
                data-lpignore="true"
                data-form-type="other"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-stone-200">
              <Button variant="outline" onClick={() => setEditingClient(null)}>
                Ακύρωση
              </Button>
              <Button onClick={handleSaveClient} className="bg-red-700 hover:bg-red-800">
                Αποθήκευση
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Financial Management Modal */}
      <FinancialManagementModal
        isOpen={isFinancialOpen}
        onClose={() => setIsFinancialOpen(false)}
        clients={clients}
        cases={cases}
        entries={entries}
      />

      {/* Ομάδα γραφείου */}
      <OfficeTeamModal open={isTeamOpen} onClose={() => setIsTeamOpen(false)} />
      <MyrtoChat />
      {showOnboarding && <OnboardingGuide userId={user?.id} onClose={() => setShowOnboarding(false)} onStart={() => setIsFormOpen(true)} />}

      {/* Time Tracker Modal */}
      <TimeTrackerModal
        isOpen={isTimeTrackerOpen}
        onClose={() => setIsTimeTrackerOpen(false)}
        clients={clients}
        cases={cases}
        entries={entries}
        onTimeEntrySaved={() => {
          setHasActiveTimer(false);
          fetchData();
        }}
      />
    </div>
  );
};

export default Dashboard;